import React, { createContext, useContext, useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import qs from 'qs';
import { API_URL } from '../config';

interface User {
  _id: string;
  email: string;
  pendingEmail?: string | null;
  role: 'admin' | 'business';
  businessName?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string) => Promise<User>;
  logout: () => void;
  updateUser: (user: User) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true
        });
        setUser(response.data.user);
      } catch (err) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
      }, {
        withCredentials: true
      });
      setUser(response.data.user);
      setError(null);
      return response.data.user;
    } catch (err) {
      setError('Invalid email or password');
      throw new Error('Login failed');
    }
  };

  const register = async (email: string, password: string): Promise<User> => {
    try {
      const requestData = { email, password };
      console.log('Client: Preparing registration request:', {
        url: `${API_URL}/api/auth/register`,
        data: { ...requestData, password: '[REDACTED]' },
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const response = await axios({
        method: 'post',
        url: `${API_URL}/api/auth/register`,
        data: requestData,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('Client: Registration response:', {
        status: response.status,
        data: response.data
      });

      setUser(response.data.user);
      setError(null);
      return response.data.user;
    } catch (err: unknown) {
      const error = err as AxiosError<{ message: string }>;
      console.error('Client: Registration error details:', {
        message: error.response?.data?.message || error.message,
        status: error.response?.status,
        data: error.response?.data,
        requestData: { email, password: '[REDACTED]' },
        error: error
      });
      setError('Registration failed');
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      setUser(null);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}; 